import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import OrganisationCard from "@components/DashBoard/OrganisationCard/OrganisationCard";
import OrganizationFinder from "@components/OrganisationFinder/OrganisationFinder";
import Loader from "@components/shared/Loader/Loader";
import { NO_ORGANISATION_IMG } from "@constants/assets";
import { FILTER_OPTIONS } from "@constants/dashboard";
import { OrganisationDataProps } from "@models/components/organisation.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import dashboardSlice from "@slices/dashboard.slice";
import { RootState } from "@store/index";

const OrganisationListComponent: React.FC = () => {
  const [organisationData, setOrganisationData] = useState<
    OrganisationDataProps[]
  >([]);
  const [filteredData, setFilteredData] = useState<OrganisationDataProps[]>([]);

  const [filterValue, setFilterValue] = useState<FILTER_OPTIONS>(
    FILTER_OPTIONS.ALL
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { accessToken } = useSelector((state: RootState) => state.local);
  const { filteredOrganisationData, isSearch } = useSelector(
    (state: RootState) => state.dashboard
  );

  const organisations =
    filteredData?.length > 0
      ? filterValue === FILTER_OPTIONS.ALL ||
        filterValue === FILTER_OPTIONS.MY_REGISTERED
        ? filteredData.filter(
            data =>
              filterValue === FILTER_OPTIONS.ALL ||
              (filterValue === FILTER_OPTIONS.MY_REGISTERED && data.isMember)
          )
        : filteredData
      : [];

  useEffect(() => {
    fetchData();
    if (accessToken) {
      setFilterValue(FILTER_OPTIONS.MY_REGISTERED);
    } else {
      setFilterValue(FILTER_OPTIONS.ALL);
    }
  }, [accessToken]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [organisationData]);

  useEffect(() => {
    if (isSearch) setFilteredData(filteredOrganisationData);
    else setFilteredData(organisationData);
  }, [isSearch, organisationData, filteredOrganisationData]);

  useEffect(() => {
    if (
      filterValue === FILTER_OPTIONS.ALL ||
      filterValue === FILTER_OPTIONS.MY_REGISTERED
    )
      resetSearch();
  }, [filterValue]);

  const fetchData = async (refresh = false) => {
    setIsLoading(true);

    await API_SERVICES.DASHBOARD.ORGANIZATION_FINDER(refresh).then(data => {
      const responseData = handleResponse(data);
      if (responseData?.ok) {
        setOrganisationData(responseData.data);
      }
      setIsLoading(false);
    });
  };

  const resetSearch = () => {
    dispatch(
      dashboardSlice.actions.setLocation({
        city: "",
        club: "",
        country: "",
        isCity: true,
        isSearch: false,
      })
    );
  };

  return (
    <>
      <Loader isLoading={isLoading} message={t("fetchingClubs")} />
      <div className="container" data-testid="organisation-list-page">
        <div className="row">
          <div className="col-sm-12">
            <OrganizationFinder
              filterChange={e => {
                setFilterValue(e);
              }}
              filterValue={filterValue}
              organisationData={organisationData}
            />
            <div className="fg-card-grid" data-testid="organisation-card">
              {organisations?.map(data => (
                <OrganisationCard
                  key={data?.clubId}
                  data={data}
                  isAuthenticated={false}
                />
              ))}
            </div>
            {organisations?.length <= 0 && !isLoading && (
              <div
                data-testid="no-organisation"
                className="text-center fg-no-organisation-img"
              >
                <img
                  src={NO_ORGANISATION_IMG}
                  alt="No Organizations Found"
                  className="mb-2"
                />
                <p className="fw-bold">{t("noOrganizationsFound")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const OrganisationList = withTranslation()(OrganisationListComponent);

export default OrganisationList;
